import axios from 'axios'

/**
 *
 * @status no delete
 * @class ChatbotService
 */
class ChatbotService {
    constructor(apiUrl) {
        this.apiUrl = apiUrl
        this.activeRequestController = null
    }

    /**
    * @author Adriana Fernandez
    * @description Get chatbot general data / configuration
    * @date 17-01-2025
    * @updatedDate 13-11-2025
    * @param {String} botConfigId
    * @returns {Object}
    * @memberof ChatbotService
    */
    async getChatbotConfig(botConfigId) {
        const response = await axios.get(
            `${this.apiUrl}/bot-config?botConfigId=${botConfigId}`
        )

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }


        return response.data.data
    }

    /**
    * @author Adriana Fernandez
    * @description Create chatbot general data / configuration
    * @date 17-01-2025
    * @updatedDate 13-11-2025
    * @param {Object} data
    * @returns {Object}
    * @memberof ChatbotService
    */
    async updateChatbotConfig(data) {
        const response = await axios.put(
            `${this.apiUrl}/bot-config`,
            data
        )

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }

        return response.data.data
    }

    /**
    * @author Adriana Fernandez
    * @description Get chatbot's knowledge files
    * @date 21-01-2025
    * @updatedDate 13-11-2025
    * @param {String} botConfigId
    * @returns {Object}
    * @memberof ChatbotService
    */
    async getKnowledgeFiles(botConfigId) {
        const response = await axios.get(
            `${this.apiUrl}/bot-knowledge-files?botConfigId=${botConfigId}`
        )

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }

        return response.data.data
    }

    /**
    * @author Adriana Fernandez
    * @description Upload knowledge files
    * @date 21-01-2025
    * @updatedDate 13-11-2025
    * @param {Object} data
    * @param {} botConfigId
    * @returns {Object}
    * @memberof ChatbotService
    */
    async uploadKnowledgeFiles(data, botConfigId) {
        const response = await axios.post(
            `${this.apiUrl}/bot-knowledge?botConfigId=${botConfigId}`,
            data
        )

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }

        return response.data.data
    }

    /**
    * @author Adriana Fernandez
    * @description Delete knowledge file
    * @date 21-01-2025
    * @updatedDate 13-11-2025
    * @param {UUID} id
    * @param {string} botConfigId
    * @returns {Object}
    * @memberof ChatbotService
    */
    async deleteKnowledgeFile(id, botConfigId) {
        const response = await axios.delete(
            `${this.apiUrl}/bot-knowledge?knowledgeFileId=${id}&botConfigId=${botConfigId}`
        )

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }

        return response.data.data
    }

    /**
    * @author Adriana Fernandez
    * @description Get all conversations
    * @date 27-01-2025
    * @updatedDate 13-11-2025
    * @param {String} botConfigId
    * @returns {Object}
    * @memberof ChatbotService
    */
    async getConversations(botConfigId) {
        const response = await axios.get(
            `${this.apiUrl}/conversations?botConfigId=${botConfigId}`
        )

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }

        return response.data.data
    }

    /**
    * @author Adriana Fernandez
    * @description Create conversation
    * @date 21-01-2025
    * @updatedDate 13-11-2025
    * @param {Object} data
    * @returns {Object}
    * @memberof ChatbotService
    */
    async createConversation(data) {
        const response = await axios.post(
            `${this.apiUrl}/bot-conversation`,
            data
        )

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }

        return response.data.data
    }

    /**
    * @author Adriana Fernandez
    * @description Get conversation
    * @date 27-01-2025
    * @updatedDate 27-01-2025
    * @param {String} botConfigId
    * @returns {Object}
    * @memberof ChatbotService
    */
    async getConversation(conversationId, botConfigId) {
        let userTimezone
        try {
            userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        } catch (error) {
            userTimezone = 'America/Mexico_City'
        }

        const response = await axios.get(
            `${this.apiUrl}/bot-conversation?conversationId=${conversationId}&botConfigId=${botConfigId}`,
            { headers: { 'X-Timezone': userTimezone}}
        )

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }

        return response.data.data
    }

    /**
    * @author Adriana Fernandez
    * @description Save conversation
    * @date 27-01-2025
    * @param {Object} data
    * @returns {Object}
    * @memberof ChatbotService
    */
    async saveConversation(data) {
        // Abort previous request if it's still active
        if (this.activeRequestController) {
            this.activeRequestController.abort()
        }

        this.activeRequestController = new AbortController()
        const controller = this.activeRequestController

        const response = await axios.post(
            `${this.apiUrl}/conversation-upsert`,
            data,
            { signal: controller.signal }
        )

        if (response && response.data && response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        } else if (response && response.data && response.data.statusCode === 200) {
            return response;
        }
    }

    /**
    * @author Jorge Perales
    * @description Delete conversation
    * @date 18-09-2025
    * @param {String} conversationId
    * @returns {Object}
    * @memberof ChatbotService
    */
    async deleteConversation(conversationId) {
        let userTimezone
        try {
            userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        } catch (error) {
            userTimezone = 'America/Mexico_City'
        }

        const response = await axios.delete(
            `${this.apiUrl}/bot-conversation?conversationId=${conversationId}`,
            { headers: { 'X-Timezone': userTimezone } }
        );

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }

        return response.data.data
    }

    

    async getAssistantTones() {
        const response = await axios.get(`${this.apiUrl}/assistant-tones`)
        if (response.data.statusCode !== 200) throw new Error(response.data.message)
        return response.data.data
    }

    /**
     * @description Upload image with type specification
     * @updatedDate 13-11-2025
     * @param {String} botConfigId
     * @param {File|String} image - File object or base64 string
     * @param {String} imageType - 'profile' or 'background'
     * @returns {Object} - Response with image URL
     */
    async uploadImage(botConfigId, image, imageType = 'profile') {
        const formData = new FormData()
        
        // Agregar tipo de imagen al formData
        formData.append('type', imageType === 'profile' ? 'assistantImage' : 'assistantBackground')
        
        if (typeof image === 'string') {
            // Si es base64, convertimos a blob
            const blob = await fetch(image).then(res => res.blob())
            formData.append('image', blob, `${imageType}-image.png`)
        } else {
            formData.append('image', image)
        }

        formData.append('botConfigId', botConfigId)
        
        const response = await axios.put(
            `${this.apiUrl}/upload-bot-image`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        )

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }

        return response.data.data
    }

    /**
     * @author Antonio Vazquez
     * @description Get chatbot by conversation ID
     * @date 31-12-2025
     * @param {String} conversationId
     * @param {String} token
     * @returns {Object}
     * @memberof ChatbotService
     */
    async getChatbotByConversationId(conversationId, token) {
        const response = await axios.get(
            `${this.apiUrl}/chatbot-by-conversationId?conversationId=${conversationId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            }
        )

        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }

        return response.data.data
    }

}


export { ChatbotService as default }